import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import { getReviewQueue } from './apiLibrary';
import { FILE_KIND_LABELS, formatDate, personName } from './libraryConstants';
import ItemActions from './ItemActions';

// Approver view: everything waiting for review, oldest first
const ReviewQueue = ({ groupId, config, refreshKey, onOpenItem, onEdit, onChanged }) => {
    const [items, setItems] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        getReviewQueue(groupId).then(res => {
            if (cancelled) return;
            if (res.error) setError(res.error);
            else { setError(''); setItems(res.items); }
        });
        return () => { cancelled = true; };
    }, [groupId, refreshKey]);

    if (error) return <Alert severity="error">{error}</Alert>;
    if (!items) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    if (!items.length) return <Typography color="text.secondary" sx={{ py: 3 }}>Nothing is waiting for review.</Typography>;

    return (
        <Stack spacing={2}>
            {items.map(item => {
                const submitted = [...(item.history || [])].reverse().find(h => h.action === 'submitted');
                return (
                    <Card key={item._id} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Box sx={{
                                    width: 96, height: 72, borderRadius: 1, bgcolor: 'grey.100', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                                }}>
                                    {item.previewUrl
                                        ? <img src={item.previewUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        : <MemoryIcon sx={{ color: 'grey.400' }} />}
                                </Box>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>{item.code} · r{item.revision}</Typography>
                                    {item.title && <Typography>{item.title}</Typography>}
                                    <Typography variant="body2" color="text.secondary">
                                        Submitted by {personName(item.submittedBy) || personName(item.createdBy) || 'a contributor'}
                                        {submitted ? ` on ${formatDate(submitted.at)}` : ''}
                                        {item.supersedes ? ' · replaces the published revision' : ''}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Files: {item.files.map(f => FILE_KIND_LABELS[f.kind]).join(', ') || 'none'}
                                    </Typography>
                                    {submitted && submitted.note && (
                                        <Alert severity="info" icon={false} sx={{ mt: 1, py: 0 }}>“{submitted.note}”</Alert>
                                    )}
                                    <Stack direction="row" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }} alignItems="flex-start">
                                        <Button size="small" variant="text" onClick={() => onOpenItem(item._id)} sx={{ textTransform: 'none' }}>View details</Button>
                                        <ItemActions groupId={groupId} config={config} item={item} onChanged={onChanged} onEdit={onEdit} />
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default ReviewQueue;
